import {
    Modal, Box, Button, Typography, Grid, MenuItem, Backdrop, Fade,
    Accordion, AccordionSummary, AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LoadingButton from "@mui/lab/LoadingButton";
import { Field, Form, Formik } from "formik";
import { TextField } from "formik-material-ui";
import { useEffect, useState, useMemo } from "react";
import * as Yup from "yup";
import axios from "axios";
import { toast } from "react-toastify";
import REACT_APP from "../../../environment";

const regex = {
    text: /^[a-zA-Z0-9\s\-.,!?()]+$/
};

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "60%",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
};

export default function UpdateAmenity({ id, handleMenuClose, getAllHotels, amenityGroups }) {
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [loading, setLoading] = useState(false);
    const [initialValues, setInitialValues] = useState(null);
    const [languages, setLanguages] = useState([]);

    const requiredLangId = 2;

    const fetchLanguages = async () => {
        try {
            const res = await axios.get(`${REACT_APP.COMMON_API}/tts/api/v01/languages`, {
                headers: {
                    "client-id": REACT_APP.COMMON_ID,
                    secret: REACT_APP.COMMON_SECRET
                },
            });
            setLanguages(res.data.data);
        } catch (err) {
            toast.error("Failed to load languages");
        }
    };

    const fetchAmenity = async () => {
        try {
            const res = await axios.get(`${REACT_APP.URL}/api/hdp/tts/adm/hotel/amenity/${id}`, {
                headers: {
                    Authorization: sessionStorage.getItem("token"),
                    "client-id": REACT_APP.CLIENTID,
                    "client-secret": REACT_APP.SECRET
                },
            });

            const amenity = res.data.data;
            const formattedTranslations = languages.reduce((acc, lang) => {
                const match = amenity.translations.find(t => t.lan_id === lang.id);
                acc[lang.id] = {
                    id: match?.id || null,
                    name: match?.name || "",
                    comment: match?.comment || ""
                };
                return acc;
            }, {});

            setInitialValues({
                group: amenity.group,
                translations: formattedTranslations
            });

            setExpanded(requiredLangId);
        } catch (err) {
            toast.error("Failed to fetch amenity");
            console.error(err);
        }
    };

    useEffect(() => {
        fetchLanguages();
    }, []);

    useEffect(() => {
        if (languages.length > 0) {
            fetchAmenity();
        }
    }, [languages]);

    const validationSchema = useMemo(() => {
        const translationShape = {};
        languages.forEach((lang) => {
            translationShape[lang.id] = Yup.object().shape({
                name: lang.id === requiredLangId
                    ? Yup.string().required("Required").matches(regex.text, "Invalid characters")
                    : Yup.string().nullable(),
                comment: Yup.string().nullable()
            });
        });

        return Yup.object().shape({
            group: Yup.string().required("Group is required"),
            translations: Yup.object().shape(translationShape),
        });
    }, [languages]);

    const handleAccordion = (langId) => (_, isExpanded) => {
        setExpanded(isExpanded ? langId : null);
    };

    const onSubmit = async (values) => {
        setLoading(true);

        const payload = {
            amenity_guid: id,
            group: values.group,
            translations: Object.entries(values.translations)
                .filter(([_, val]) => val.name?.trim())
                .map(([langId, val, id]) => ({
                    id: parseInt(val.id),
                    lan_id: parseInt(langId),
                    name: val.name,
                    comment: val.comment
                }))
        };

        try {
            await axios.put(`${REACT_APP.URL}/api/hdp/tts/adm/hotel/amenity`, payload, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: sessionStorage.getItem("token"),
                    "client-id": REACT_APP.CLIENTID,
                    "client-secret": REACT_APP.SECRET,
                }
            });
            toast.success("Amenity updated successfully", { autoClose: 2000 });
            getAllHotels();
            handleMenuClose();
            setOpen(false);
        } catch (err) {
            toast.error("Update failed");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Button onClick={() => setOpen(true)}>Update</Button>

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{ backdrop: { timeout: 500 } }}
            >
                <Fade in={open}>
                    <Box sx={modalStyle}>
                        <Typography variant="h6" mb={2}>Update Amenity</Typography>

                        {initialValues && (
                            <Formik
                                enableReinitialize
                                initialValues={initialValues}
                                validationSchema={validationSchema}
                                onSubmit={onSubmit}
                            >
                                <Form autoComplete="off">
                                    {/* Group dropdown */}
                                    <Grid item xs={12} sx={{ mb: 2 }}>
                                        <Field
                                            fullWidth
                                            select
                                            name="group"
                                            component={TextField}
                                            label="Amenity Group"
                                        >
                                            {amenityGroups.map((item) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </Grid>

                                    {/* Translations accordion */}
                                    {languages.map((lang) => (
                                        <Accordion
                                            key={lang.id}
                                            expanded={expanded === lang.id}
                                            onChange={handleAccordion(lang.id)}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Typography>{lang.name}</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                        <Field
                                                            fullWidth
                                                            name={`translations.${lang.id}.name`}
                                                            label="Amenity Name"
                                                            component={TextField}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Field
                                                            fullWidth
                                                            name={`translations.${lang.id}.comment`}
                                                            label="Amenity Comment"
                                                            component={TextField}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}

                                    <Box display="flex" justifyContent="space-between" mt={3}>
                                        <Button variant="contained" color="error" onClick={() => setOpen(false)}>
                                            Close
                                        </Button>
                                        {loading ? (
                                            <LoadingButton loading variant="contained" color="success">
                                                Update
                                            </LoadingButton>
                                        ) : (
                                            <Button type="submit" variant="contained" color="success">
                                                Update
                                            </Button>
                                        )}
                                    </Box>
                                </Form>
                            </Formik>
                        )}
                    </Box>
                </Fade>
            </Modal>
        </div>
    );
}
